<template>
  <div class="server-vhosts-tab">
    <div class="server-vhosts-tab__card">
      <div class="server-vhosts-tab__card-head">
        <h4>Domains &amp; vhosts</h4>
        <div class="server-vhosts-tab__head-right">
          <span v-if="vhosts.length" class="server-vhosts-tab__n">{{ vhosts.length }} vhost{{ vhosts.length === 1 ? '' : 's' }}</span>
          <span v-if="collectedAt" class="server-vhosts-tab__fetched">fetched {{ formatRelativeTime(collectedAt) }}</span>
          <button type="button" class="server-vhosts-tab__btn" :disabled="refreshing" @click="refresh">
            {{ refreshing ? 'Refreshing…' : 'Refresh' }}
          </button>
        </div>
      </div>
      <p v-if="error" class="server-vhosts-tab__error">{{ error }}</p>

      <table>
        <thead>
          <tr><th>Domain</th><th>Aliases</th><th>Docroot</th><th>SSL</th><th>Cert expiry</th></tr>
        </thead>
        <tbody>
          <tr v-for="vhost in vhosts" :key="`${vhost.domain}-${vhost.config_file}`">
            <td>
              <strong>{{ vhost.domain }}</strong>
              <div class="server-vhosts-tab__conf">{{ vhost.config_file }}</div>
            </td>
            <td>
              <span v-if="vhost.aliases && vhost.aliases.length">{{ vhost.aliases.join(', ') }}</span>
              <span v-else class="server-vhosts-tab__na">—</span>
            </td>
            <td class="server-vhosts-tab__mono">{{ vhost.docroot || '—' }}</td>
            <td>
              <span class="server-vhosts-tab__ssl" :class="vhost.ssl ? 'is-on' : 'is-off'">{{ vhost.ssl ? 'SSL' : 'none' }}</span>
            </td>
            <td>
              <span
                v-if="vhost.cert_expires_at"
                class="server-vhosts-tab__expiry"
                :class="expiryClass(vhost.cert_expires_at)"
              >{{ formatExpiry(vhost.cert_expires_at) }}</span>
              <span v-else class="server-vhosts-tab__na">—</span>
            </td>
          </tr>
          <tr v-if="loaded && !vhosts.length">
            <td colspan="5" class="server-vhosts-tab__empty">
              No vhost data reported yet — refresh to ask the agent (requires agent v3.0.23-staging.11+).
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import useIntrospection from '@root/hooks/useIntrospection';
import { formatRelativeTime } from '@root/services/serverFormat';

// `server-detail.html`'s Domains & vhosts tab — renders the agent's
// `vhost.list` result (one row per Apache <VirtualHost> block). Certs
// expiring within 30 days are highlighted; already-expired certs read as
// critical. Refresh re-issues the command via the shared useIntrospection hook.
const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
});

const { data, collectedAt, loaded, refreshing, error, load, refresh } = useIntrospection(props.apiBase, props.serverId, 'vhost.list');

const vhosts = computed(() => (Array.isArray(data.value?.vhosts) ? data.value.vhosts : []));

function daysUntil(iso) {
  const expiry = new Date(iso).getTime();
  if (Number.isNaN(expiry)) {
    return null;
  }
  return Math.floor((expiry - Date.now()) / 86400000);
}

function expiryClass(iso) {
  const days = daysUntil(iso);
  if (days === null) {
    return '';
  }
  if (days < 0) {
    return 'is-expired';
  }
  if (days < 30) {
    return 'is-expiring';
  }
  return 'is-ok';
}

function formatExpiry(iso) {
  const days = daysUntil(iso);
  const date = new Date(iso);
  const dateLabel = Number.isNaN(date.getTime()) ? iso : date.toISOString().slice(0, 10);
  if (days === null) {
    return dateLabel;
  }
  if (days < 0) {
    return `${dateLabel} (expired ${Math.abs(days)}d ago)`;
  }
  return `${dateLabel} (${days}d)`;
}

onMounted(load);
</script>

<style lang="scss">
.server-vhosts-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__card-head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__head-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__n, &__fetched {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__btn {
    font-size: 11.5px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: var(--theme-radius, 6px);
    cursor: pointer;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__error {
    color: #a53232;
    font-size: 12px;
    padding: 10px 18px;
    margin: 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    padding: 8px 18px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  td {
    padding: 8px 18px;
    font-size: 12.5px;
    border-top: 1px solid var(--theme-border-soft);
    vertical-align: top;
    word-break: break-word;
  }

  &__conf {
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__mono {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
  }

  &__na {
    color: var(--theme-text-tertiary);
  }

  &__ssl {
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-transform: uppercase;
    background: var(--theme-border-soft);
    color: var(--theme-text-secondary);

    &.is-on { background: #e1f3ea; color: #1c7a4d; }
  }

  &__expiry {
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;

    &.is-ok { color: var(--theme-text-secondary); }
    &.is-expiring {
      color: #8a5a0d;
      background: #fbeeda;
      padding: 2px 7px;
      border-radius: 3px;
      font-weight: 600;
    }
    &.is-expired {
      color: #a53232;
      background: #f8dede;
      padding: 2px 7px;
      border-radius: 3px;
      font-weight: 600;
    }
  }

  &__empty {
    color: var(--theme-text-tertiary);
    padding: 16px 18px;
    text-align: center;
  }
}
</style>
